import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Shield, Wrench, Battery } from "lucide-react";
import api from "@/api/client";
import { ProductCard } from "@/components/ProductCard";

const features = [
  { icon: Zap, title: "High-Voltage Ready", desc: "Parts engineered for the torque and heat of electric drivetrains." },
  { icon: Shield, title: "CNC Precision", desc: "Billet aluminum and carbon fiber built to tolerances that matter." },
  { icon: Wrench, title: "Direct Bolt-On", desc: "No fabrication. No guesswork. Drop-in fit for your e-moto." },
  { icon: Battery, title: "Proven Range", desc: "Tested on Sur-Ron, Talaria, Stark, and E-Ride platforms." },
];

export function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products?category=all").then(({ data }) => {
      setProducts(data.filter((p) => p.featured).slice(0, 4));
    }).catch(() => {});
  }, []);

  return (
    <div className="pt-[73px]">
      <section className="relative flex min-h-[85vh] items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="h-full w-full object-cover"
            data-testid="hero-video"
          >
            <source src="/videos/hero-1.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/50" />
          <div className="hero-glow absolute inset-0" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-4 py-24 sm:px-8">
          <div className="max-w-3xl">
            <p className="mb-4 font-mono text-sm uppercase tracking-widest text-volt animate-fade-in" data-testid="hero-tagline">
              Tactical Electric Supermoto & Performance Parts
            </p>
            <h1 className="font-display text-5xl font-black uppercase leading-none tracking-tight text-white sm:text-6xl lg:text-8xl" data-testid="hero-heading">
              Build Your<br />
              <span className="text-volt">Electric</span> Beast
            </h1>
            <p className="mt-6 max-w-xl text-lg text-slate-300" data-testid="hero-subheading">
              Motor covers, handlebars, grips, supermoto wheels, and batteries — everything you need to dominate the streets on two wheels.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/shop"
                className="group flex items-center gap-2 bg-volt px-8 py-4 font-mono text-sm font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-white"
                data-testid="hero-shop-button"
              >
                Shop Parts
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="border border-white/30 px-8 py-4 font-mono text-sm font-bold uppercase tracking-widest text-white transition-colors hover:border-volt hover:text-volt"
                data-testid="hero-contact-button"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="relative flex min-h-[70vh] items-center justify-center overflow-hidden border-y border-white/10" data-testid="action-video-section">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="action-video"
        >
          <source src="/videos/hero-2.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/55" />
        <div className="hero-glow absolute inset-0" />
        <div className="relative mx-auto max-w-4xl px-4 py-24 text-center sm:px-8">
          <p className="font-mono text-sm uppercase tracking-widest text-volt">Built in Detroit</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-6xl" data-testid="action-video-heading">
            Ride Electric.<br />Ride <span className="text-volt">Harder.</span>
          </h2>
          <Link
            to="/shop"
            className="mt-10 inline-flex items-center gap-2 bg-volt px-8 py-4 font-mono text-sm font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-white"
            data-testid="action-video-shop-button"
          >
            Shop Parts <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section className="border-b border-white/10 bg-surface py-16" data-testid="features-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f, i) => (
              <div key={f.title} className="border border-white/10 bg-obsidian p-6 animate-fade-in" style={{ animationDelay: `${i * 100}ms` }} data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <f.icon className="h-8 w-8 text-volt" />
                <h3 className="mt-4 font-display text-xl font-bold uppercase text-white">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-400">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="featured-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="mb-12 flex items-end justify-between">
            <div>
              <p className="font-mono text-sm uppercase tracking-widest text-volt">Featured</p>
              <h2 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">Top Shelf Parts</h2>
            </div>
            <Link to="/shop" className="hidden items-center gap-2 font-mono text-sm uppercase tracking-widest text-slate-400 transition-colors hover:text-volt sm:flex" data-testid="featured-view-all">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-surface py-24" data-testid="cta-section">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-8">
          <h2 className="font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">
            Ready to <span className="text-volt">Upgrade</span>?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-slate-400">
            From Sur-Ron to Stark Varg, we stock the parts that keep you pinned. Browse the full catalog and build your dream e-moto.
          </p>
          <Link
            to="/shop"
            className="mt-8 inline-flex items-center gap-2 bg-volt px-8 py-4 font-mono text-sm font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-white"
            data-testid="cta-shop-button"
          >
            Browse Catalog <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-obsidian py-12" data-testid="footer">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <img src="/logo.png" alt="DetroitEmoto" className="h-16 w-auto" data-testid="footer-logo" />
            <p className="font-mono text-xs uppercase tracking-widest text-slate-500">
              Electric Supermoto Parts — Built in Detroit
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
